import { createReducer, createAction } from 'redux-act';
import { createSelector } from 'reselect';
import { selectCurrentId } from '@gandi/redux-sharing';
import { createResource } from '@gandi/resource/redux';
import { selectCurrency } from './prepaid';
import { selectRaw as selectRawState } from './utils';
export const CC_AMEX = 'gc2';
export const CC_JCB = 'gc125';
const unsavableCreditCardsKeys = [CC_AMEX, CC_JCB];
export const resource = createResource('billing.money.means');
export const defaultCode = 'check';
export const selectIsFetching = (state, {
  sharingId
}) => resource.selectIsFetching(state, sharingId);
export const selectIsLoaded = (state, {
  sharingId
}) => resource.selectIsLoaded(state, sharingId);
export const selectIsFailure = (state, {
  sharingId
}) => resource.selectIsFailure(state, sharingId);
export const selectIsToRefetch = (state, {
  sharingId
}) => resource.selectIsToRefetch(state, sharingId);
export const selectRaw = createSelector([selectRawState], raw => raw.means);
export const selectExcludeProp = (_, props = Object.freeze({})) => props?.exclude;
export const selectIncludeProp = (_, props = Object.freeze({})) => props?.include;
export const selectContextualMeans = createSelector([(_, filters) => filters, selectRaw], (filters, raw = {}) => {
  const {
    label,
    include,
    exclude
  } = filters;
  const rawMeans = raw[label];
  const means = rawMeans?.filter(mean => !(exclude ? exclude.some(code => code === mean.code || code === mean.provider) : false)).filter(mean => include ? include.some(code => code === mean.code || code === mean.provider) : true);
  return Array.isArray(rawMeans) ? means : undefined;
});
export const selectMeans = createSelector([selectRaw, selectCurrentId, selectExcludeProp, selectIncludeProp], (raw = {}, sharingId, toExclude, toInclude) => {
  let means = raw[sharingId];
  if (!Array.isArray(means) || !means.length) {
    return undefined;
  }
  if (Array.isArray(toExclude) && toExclude.length > 0) {
    means = means.filter(mean => toExclude.indexOf(mean.code) === -1 && toExclude.indexOf(mean.provider) === -1);
  }
  if (Array.isArray(toInclude) && toInclude.length > 0) {
    means = means.filter(mean => toInclude.indexOf(mean.code) > -1 || toInclude.indexOf(mean.provider) > -1);
  }
  return means;
});
export const selectMean = (state, props = {}) => {
  const meanArray = selectMeans(state, {
    include: [props.code]
  });
  if (Array.isArray(meanArray) && meanArray[0]) {
    return meanArray[0];
  }
  return undefined;
};
export const selectProvider = createSelector([selectMean], mean => mean ? mean.provider : undefined);
export const fakeCreditCardMean = {
  allow_recurrent: false,
  allow_embedded: false,
  name_label: 'Credit Card',
  name: 'Credit Card',
  code: 'cc',
  key: 'fakecc',
  provider: 'gc',
  pay_url: ''
};
export const selectHasMultipleCreditCardMeans = state => {
  const creditCardMeans = selectMeans(state, {
    include: ['cc', 'dropin']
  });
  if (!Array.isArray(creditCardMeans) || !creditCardMeans.length) {
    return undefined;
  }
  if (creditCardMeans.length === 1) {
    return false;
  }
  return true;
};
export const selectMainMeans = createSelector([selectMeans, selectHasMultipleCreditCardMeans], (means, hasMultipleCreditCardMeans) => {
  if (!Array.isArray(means)) {
    return undefined;
  }
  if (!hasMultipleCreditCardMeans) {
    return means;
  }
  return means.filter(mean => !['cc', 'dropin'].includes(mean.code)).concat(fakeCreditCardMean);
});
export const selectMultipleCreditCardMeans = createSelector([selectMeans, selectHasMultipleCreditCardMeans], (means, hasMultipleCreditCardMeans) => {
  if (!Array.isArray(means) || !hasMultipleCreditCardMeans) {
    return undefined;
  }
  return means.filter(mean => ['cc', 'dropin'].includes(mean.code));
});
export const selectCreditCardProvider = state => selectProvider(state, {
  code: 'cc'
}) || selectProvider(state, {
  code: 'dropin'
});
export const selectCreditCardPaymentProductIds = createSelector([selectMultipleCreditCardMeans], multipleCreditCardMeans => {
  if (!multipleCreditCardMeans) {
    return undefined;
  }
  return multipleCreditCardMeans.map(mean => Number.parseInt(mean.key.split(mean.provider)[1], 10));
});
export const selectSavableCreditCards = state => {
  const creditCards = selectMeans(state, {
    include: ['cc', 'dropin']
  });
  return creditCards && creditCards.filter(cc => !unsavableCreditCardsKeys.includes(cc.key));
};
export const invalidatePaymentMeans = createAction('[redux-billing][means] Invalidate means data');
export const invalidateMeans = props => dispatch => {
  dispatch(resource.invalidate());
  dispatch(invalidatePaymentMeans(props));
};
export const fetchMeans = (params = Object.freeze({}), options = {
  sharingId: undefined,
  params: undefined
}) => (dispatch, getState) => {
  const currentId = selectCurrentId(getState());
  const sharingId = options.sharingId || currentId;
  if (typeof sharingId !== 'string') {
    return Promise.reject(new Error('[billing.infos] `fetchInfos` called without `sharingId` option' + ' nor @gandi/redux-sharing dep/state.'));
  }
  const currency = params.currency || selectCurrency(getState(), {
    sharingId
  });
  return dispatch(resource.fetch('/money/means', {
    ...options,
    label: sharingId,
    params: {
      ...(options.params || {}),
      context: params.context || 'pay',
      currency,
      sharing_id: sharingId
    },
    on200: response => response,
    not200: response => Promise.reject(response)
  }));
};
const reducer = createReducer({
  [resource.fetch.success.getType()]: (state, {
    response: {
      data
    },
    label
  }) => ({
    ...state,
    [label]: data
  }),
  [invalidatePaymentMeans.getType()]: (state, {
    sharingId
  }) => {
    if (!state[sharingId]) {
      return state;
    }
    const newState = {
      ...state
    };
    delete newState[sharingId];
    return newState;
  }
}, {});
export default reducer;